/**
 * The READ side of a template — the bordered viewer with the header strip, the
 * lavender subject bar and the gradient stage the email card lifts off.
 *
 * This is the one and only implementation: the Templates tab and the campaign
 * wizard's template step both render THIS component, so a template can never
 * look like two different things depending on where you opened it.
 */
import React from 'react'
import { relativeTime } from '@/lib/format'
import { resolveFromName, resolveReplyTo } from '@shared/message'
import { customMergeFields } from '@shared/render'
import type { RenderedEmail } from '@shared/render'
import type { Campaign, Lead, SmtpAccountSafe, Template } from '@shared/types'

/** Stand-in values when a campaign has no leads yet — previews still need one. */
export const SAMPLE_LEAD: Pick<Lead, 'name' | 'email' | 'fields'> = {
  name: 'Maria Chen',
  email: 'maria@northwind.io',
  fields: { first_name: 'Maria', company: 'Northwind' },
}

/** Always offered in the merge dropdown; custom lead columns are appended. */
export const BASE_FIELDS = ['first_name', 'name', 'email', 'company']

/** BASE_FIELDS plus every extra key the campaign's leads actually carry. */
export function mergeFieldsFromLeads(rows: Lead[]): string[] {
  const keys: string[] = []
  for (const r of rows) {
    for (const k of Object.keys(r.fields ?? {})) {
      if (!keys.includes(k) && !BASE_FIELDS.includes(k)) keys.push(k)
    }
  }
  return [...BASE_FIELDS, ...keys]
}

/** The lead a preview renders as: a real one when there is one. */
export function previewLead(rows: Lead[]): Pick<Lead, 'name' | 'email' | 'fields'> {
  return rows[0] ?? SAMPLE_LEAD
}

/**
 * SAMPLE_LEAD with a stand-in value for every custom token the template
 * merges, so a campaign with NO leads yet can still preview {{role}} without
 * the editor flagging tokens no lead exists to satisfy. Only for the empty
 * roster — a real lead is never synthesized over, so a genuine gap keeps
 * showing exactly as the send guard would report it.
 */
export function synthesizeSampleLead(
  template: Pick<Template, 'subject' | 'body_text' | 'body_html'> | null | undefined,
): Pick<Lead, 'name' | 'email' | 'fields'> {
  const fields = { ...SAMPLE_LEAD.fields }
  for (const token of customMergeFields(template)) {
    if (!(fields[token] ?? '').trim()) {
      // "job_title" -> "Job title": reads naturally mid-sentence in a preview.
      const human = token.replace(/_/g, ' ')
      fields[token] = human.charAt(0).toUpperCase() + human.slice(1)
    }
  }
  return { ...SAMPLE_LEAD, fields }
}

/**
 * Who this campaign would actually send as. Uses the SAME precedence helpers
 * the send engine uses (campaign override -> mailbox default -> local part), so
 * a viewer header can't claim something the sender wouldn't do.
 */
export function senderPreview(
  campaign: Pick<Campaign, 'smtp_account_id' | 'from_name' | 'reply_to'> | null | undefined,
  identities: SmtpAccountSafe[],
): { fromName: string | null; replyTo: string | null } {
  const identity =
    identities.find((i) => i.id === campaign?.smtp_account_id) ??
    identities.find((i) => i.is_default)
  if (!identity) return { fromName: null, replyTo: null }

  const override = campaign
    ? { from_name: campaign.from_name, reply_to: campaign.reply_to }
    : undefined

  return {
    fromName: resolveFromName(
      { from_email: identity.from_email, from_name: identity.from_name },
      override,
    ),
    replyTo:
      resolveReplyTo(
        { from_email: identity.from_email, reply_to: identity.reply_to },
        override,
      ) ?? null,
  }
}

/**
 * The rendered email at its NATURAL height, so a normal-length message is
 * fully visible and only the stage scrolls.
 *
 * `sandbox="allow-same-origin"` (and nothing else) is what makes measuring
 * possible: scripts are still blocked, so the document is inert HTML and cannot
 * touch this page — allow-same-origin merely lets the parent read its height.
 * Granting allow-scripts alongside it would be unsafe; we never do.
 */
export function AutoHeightEmail({ html }: { html: string }) {
  const ref = React.useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = React.useState(560)

  const measure = React.useCallback(() => {
    const doc = ref.current?.contentDocument
    if (!doc?.body) return
    const next = Math.max(doc.documentElement?.scrollHeight ?? 0, doc.body.scrollHeight)
    if (next > 0) setHeight(next)
  }, [])

  // Re-measure when the markup changes, and again shortly after so late
  // layout (web fonts, images) is included.
  React.useEffect(() => {
    measure()
    const t = window.setTimeout(measure, 120)
    return () => window.clearTimeout(t)
  }, [html, measure])

  return (
    <iframe
      ref={ref}
      title="Rendered email"
      sandbox="allow-same-origin"
      srcDoc={html}
      onLoad={measure}
      style={{ display: 'block', width: '100%', height, border: 'none', background: '#fff' }}
    />
  )
}

/**
 * ONE bordered box: header strip (who it renders as + host actions), subject
 * bar (merged subject + the real from/reply-to), then the tinted stage.
 */
export function TemplateViewer({
  rendered,
  sampleEmail,
  fromName,
  replyTo,
  actions,
  style,
}: {
  rendered: RenderedEmail | null
  sampleEmail: string
  fromName?: string | null
  replyTo?: string | null
  /** Buttons for the right of the header strip (Edit, Delete, Use this one…). */
  actions?: React.ReactNode
  style?: React.CSSProperties
}) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid var(--border-default)',
        borderRadius: 10,
        overflow: 'hidden',
        background: 'var(--surface-box)',
        ...style,
      }}
    >
      {/* 1) header strip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 16px',
          background: 'var(--surface-box-header)',
          borderBottom: '1px solid var(--border-default)',
          flex: 'none',
        }}
      >
        <span
          style={{
            fontSize: 12.5,
            color: 'var(--text-muted)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          Previewing as{' '}
          <span style={{ color: 'var(--accent-text)', fontWeight: 600 }}>{sampleEmail}</span>
        </span>
        <div style={{ flex: 1 }} />
        {actions}
      </div>

      {/* 2) subject bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 18px',
          background: 'linear-gradient(90deg,#eceafb,#f3f0fb)',
          borderBottom: '1px solid var(--border-default)',
          flex: 'none',
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            color: 'var(--accent-text)',
            background: '#dedcfb',
            borderRadius: 4,
            padding: '2px 7px',
            letterSpacing: '.04em',
            flex: 'none',
          }}
        >
          SUBJECT
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 180,
            fontSize: 16.5,
            fontWeight: 600,
            color: '#1c1f2e',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {rendered?.subject || '(no subject)'}
        </span>
        <span
          style={{
            display: 'inline-flex',
            gap: 12,
            fontSize: 12,
            color: 'var(--text-muted)',
            flexWrap: 'wrap',
          }}
        >
          {fromName && (
            <span>
              from <span style={{ color: 'var(--accent-text)', fontWeight: 600 }}>{fromName}</span>
            </span>
          )}
          {replyTo && (
            <span>
              reply-to{' '}
              <span style={{ color: 'var(--accent-text)', fontWeight: 600 }}>{replyTo}</span>
            </span>
          )}
        </span>
      </div>

      {/* 3) stage — tinted canvas the email card lifts off */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          padding: 22,
          background:
            'radial-gradient(1200px 400px at 50% -10%, #e9e8fb 0%, #efeadf 55%, #efeadf 100%)',
        }}
      >
        {rendered && (
          <div
            style={{
              maxWidth: 640,
              margin: '0 auto',
              background: '#fff',
              border: '1px solid #e6e8ec',
              borderRadius: 12,
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(66,54,140,.14), 0 2px 6px rgba(66,54,140,.08)',
            }}
          >
            {/* Viewer chrome only. Deliberately OUTSIDE the rendered email —
                it never reaches renderEmail(), so no send can carry it. */}
            <div
              aria-hidden
              style={{ height: 5, background: 'linear-gradient(90deg,#6f6ce6,#5b5bd6,#8a7bf0)' }}
            />
            <AutoHeightEmail html={rendered.html} />
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * The template picker rail — the campaign's templates, newest first. Shared so
 * the wizard picks a template exactly the way the Templates tab does.
 */
export function TemplateList({
  templates,
  activeId,
  onSelect,
  boundId,
  narrow,
  style,
}: {
  templates: Template[]
  activeId: string | null
  onSelect: (t: Template) => void
  /** Marked "in use": the one the sender picks for new queue rows. */
  boundId?: string | null
  narrow?: boolean
  style?: React.CSSProperties
}) {
  return (
    <div
      style={{
        width: narrow ? '100%' : 230,
        flex: 'none',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflowY: narrow ? 'visible' : 'auto',
        maxHeight: narrow ? 168 : undefined,
        ...style,
      }}
    >
      {templates.map((t) => (
        <TemplateItem
          key={t.id}
          template={t}
          active={t.id === activeId}
          bound={boundId ? t.id === boundId : false}
          onClick={() => onSelect(t)}
        />
      ))}
    </div>
  )
}

function TemplateItem({
  template,
  active,
  bound,
  onClick,
}: {
  template: Template
  active: boolean
  bound: boolean
  onClick: () => void
}) {
  const [hover, setHover] = React.useState(false)
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '7px 10px',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        background: active || hover ? 'var(--surface-sunken)' : 'transparent',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <span
          style={{
            flex: 1,
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {template.name?.trim() || template.subject || '(no subject)'}
        </span>
        {bound && (
          <span
            title="This is the template new queue rows are created with"
            style={{
              flex: 'none',
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '.04em',
              padding: '1px 6px',
              borderRadius: 999,
              background: 'var(--accent-soft)',
              color: 'var(--accent-text)',
            }}
          >
            IN USE
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 12,
          color: 'var(--text-muted)',
          marginTop: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {/* named templates show their subject here; unnamed ones show the age */}
        {template.name?.trim() ? template.subject : relativeTime(template.created_at)}
      </div>
    </div>
  )
}
